import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Clock, CheckCircle2, XCircle, AlertTriangle, BookMarked, MapPin } from "lucide-react";
import { circulationApi, type HoldRecord, type PaginatedResponse } from "@/lib/api/circulation";

const statusConfig: Record<string, { label: string; cls: string; description: string }> = {
  pending: {
    label: "In Queue",
    cls: "bg-yellow-100 text-yellow-700",
    description: "Waiting for an available copy",
  },
  ready: {
    label: "Ready for Pickup",
    cls: "bg-green-100 text-green-700",
    description: "Your item is on the hold shelf",
  },
  fulfilled: {
    label: "Checked Out",
    cls: "bg-blue-100 text-blue-600",
    description: "You've picked up this item",
  },
  cancelled: {
    label: "Cancelled",
    cls: "bg-gray-100 text-gray-500",
    description: "This hold was cancelled",
  },
  expired: {
    label: "Expired",
    cls: "bg-red-100 text-red-600",
    description: "Pickup period expired",
  },
};

type TabFilter = "active" | "past";

const MyHoldsPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [data, setData] = useState<PaginatedResponse<HoldRecord> | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabFilter>("active");
  const [page, setPage] = useState(1);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    if (user?.id) loadHolds();
  }, [user?.id, tab, page]);

  const loadHolds = async () => {
    setLoading(true);
    try {
      const result = await circulationApi.getHolds({
        userId: user?.id,
        page,
        limit: 20,
      });

      if (tab === "active") {
        result.data = result.data.filter((h) => h.status === "pending" || h.status === "ready");
      } else {
        result.data = result.data.filter(
          (h) => h.status === "fulfilled" || h.status === "cancelled" || h.status === "expired"
        );
      }
      result.total = result.data.length;

      setData(result);
    } catch {
      toast.error("Failed to load your holds");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelId) return;
    setCancelLoading(true);
    try {
      await circulationApi.cancelHold(cancelId);
      toast.success("Hold cancelled");
      setCancelId(null);
      loadHolds();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to cancel hold");
    } finally {
      setCancelLoading(false);
    }
  };

  const getTitle = (hold: HoldRecord) =>
    hold.item?.biblio?.title ?? hold.biblio?.title ?? "Unknown Title";

  const holds = data?.data ?? [];

  return (
    <AppLayout>
      <PageContainer>
        <PageHeader
          title="My Holds"
          description="Track your reserved items"
          breadcrumbItems={[{ label: "Dashboard", href: "/dashboard" }, { label: "My Holds" }]}
          showSearch={false}
          titleSize="md"
        />

        {/* Tabs */}
        <div className="flex bg-muted/50 rounded-lg p-1 gap-1 w-fit">
          {(["active", "past"] as TabFilter[]).map((t) => (
            <button
              key={t}
              onClick={() => {
                setTab(t);
                setPage(1);
              }}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
                tab === t
                  ? "bg-background shadow font-medium"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {t === "active" ? "Active Holds" : "Past Holds"}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : holds.length === 0 ? (
          <Card className="p-8 text-center text-muted-foreground">
            <BookMarked className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="font-medium">{tab === "active" ? "No active holds" : "No past holds"}</p>
            <p className="text-sm mt-1">
              {tab === "active"
                ? "You can place a hold on any unavailable item from the catalog."
                : "Your completed, cancelled, and expired holds will appear here."}
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {holds.map((hold) => {
              const cfg = statusConfig[hold.status] ?? statusConfig.pending;
              const isReady = hold.status === "ready";

              return (
                <Card
                  key={hold.id}
                  className={`p-4 ${isReady ? "border-green-300 bg-green-50/30" : ""}`}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${cfg.cls}`}
                    >
                      {isReady ? (
                        <CheckCircle2 className="w-5 h-5" />
                      ) : hold.status === "pending" ? (
                        <Clock className="w-5 h-5" />
                      ) : hold.status === "expired" ? (
                        <AlertTriangle className="w-5 h-5" />
                      ) : (
                        <XCircle className="w-5 h-5" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm line-clamp-2">{getTitle(hold)}</h3>

                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <Badge variant="secondary" className={`${cfg.cls} text-xs`}>
                          {cfg.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{cfg.description}</span>
                      </div>

                      <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground flex-wrap">
                        <span>Placed: {new Date(hold.reservedAt).toLocaleDateString()}</span>
                        {hold.pickupBranch && (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            Pickup: {hold.pickupBranch.name}
                          </span>
                        )}
                        {hold.expiresAt && (
                          <span className={isReady ? "text-green-700 font-medium" : ""}>
                            {isReady ? "Pick up by" : "Expires"}:{" "}
                            {new Date(hold.expiresAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      {isReady && (
                        <div className="mt-2 p-2 bg-green-100 rounded-lg text-sm text-green-800 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 shrink-0" />
                          <span>
                            Your item is ready!{" "}
                            {hold.expiresAt && (
                              <>
                                Please pick it up by {new Date(hold.expiresAt).toLocaleDateString()}
                                .
                              </>
                            )}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Cancel (only for pending/ready) */}
                    {(hold.status === "pending" || hold.status === "ready") && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setCancelId(hold.id)}
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {data.page} of {data.totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= data.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </PageContainer>

      {/* Cancel confirmation */}
      <Dialog open={!!cancelId} onOpenChange={() => setCancelId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Hold</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this hold? You'll lose your place in the queue.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => setCancelId(null)}>
              Keep Hold
            </Button>
            <Button variant="destructive" onClick={handleCancel} disabled={cancelLoading}>
              {cancelLoading ? <LoadingSpinner /> : "Cancel Hold"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default MyHoldsPage;
